import { DOMESTIC_DATA, getOverseasData, type BirthPlaceTree } from '../birthPlaceData';
import { toSimplified } from '@/utils/langConvert';

/** 无实际意义的通用区县，展示时省略，但存值时仍保留以兼容旧逻辑 */
const GENERIC_DISTRICTS = new Set(['全市/县', '全区']);

export interface PlaceOption {
  /** 存值：以空格分隔的完整地名，如 "上海市 上海市 长宁区" */
  value: string;
  /** 展示串：以 "-" 连接，如 "上海市-上海市-长宁区" */
  label: string;
  /** 展示片段数组，用于搜索高亮 */
  segments: string[];
  /** 搜索匹配串：label 归一化（简体 + 小写），索引构建时预计算，避免每次按键逐条转换 */
  search: string;
}

function flattenTree(tree: BirthPlaceTree): PlaceOption[] {
  const list: PlaceOption[] = [];
  Object.keys(tree).forEach((province) => {
    const cityMap = tree[province] ?? {};
    Object.keys(cityMap).forEach((city) => {
      const districts = cityMap[city] ?? [];
      districts.forEach((district) => {
        const valueParts = [province, city, district];
        const segments = GENERIC_DISTRICTS.has(district)
          ? [province, city]
          : [province, city, district];
        list.push({
          value: valueParts.join(' '),
          label: segments.join('-'),
          segments,
          search: toSimplified(segments.join('-')).toLowerCase(),
        });
      });
    });
  });
  return list;
}

let domesticCache: PlaceOption[] | null = null;

/** 国内地名搜索索引（进程内缓存） */
export function getDomesticPlaces(): PlaceOption[] {
  if (!domesticCache) domesticCache = flattenTree(DOMESTIC_DATA);
  return domesticCache;
}

const overseasCache: Record<string, PlaceOption[]> = {};

/** 海外地名搜索索引（按语言缓存） */
export function getOverseasPlaces(lang: string): PlaceOption[] {
  if (!overseasCache[lang]) overseasCache[lang] = flattenTree(getOverseasData(lang));
  return overseasCache[lang];
}

/**
 * 将查询词归一化为简体小写后再匹配。
 * 地名索引固定为简体，繁体（zh-TW/zh-HK）模式下用户输入的繁体字需先转回简体，否则搜不出结果。
 */
export function normalizeSearchQuery(query: string): string {
  return toSimplified(query).trim().toLowerCase();
}

/** 按关键词过滤地名，命中即包含（大小写不敏感、简繁不敏感），限制返回数量 */
export function filterPlaces(places: PlaceOption[], query: string, limit = 80): PlaceOption[] {
  const q = normalizeSearchQuery(query);
  if (!q) return [];
  const out: PlaceOption[] = [];
  for (let i = 0; i < places.length; i += 1) {
    if (places[i].search.includes(q)) {
      out.push(places[i]);
      if (out.length >= limit) break;
    }
  }
  return out;
}